import { useEffect, useState } from "react";
import { Plug, Trash2, RefreshCw, Loader2, ExternalLink, Globe, Webhook, BarChart3, LineChart as LineIcon, Info, ShoppingBag, Layout, Ghost, Frame, Squirrel, ArrowUpCircle, Layers, Package, Send, Settings, CheckCircle2, Code2, Server, Copy, Check, X, Megaphone } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { useProjects } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// All integration definitions in one place.
const KIND_META = {
  // CMS publishing
  wordpress:   { label: "WordPress",     icon: Globe,        color: "#21759B", desc: "Auto-publish blogs to your WordPress site via REST API.",        group: "cms",  live: true,  fields: ["site_url", "username", "app_password"] },
  shopify:     { label: "Shopify",       icon: ShoppingBag,  color: "#5C6AC4", desc: "Auto-publish to your Shopify store blog via Admin API.",         group: "cms",  live: true,  fields: ["store_domain", "access_token", "blog_id"] },
  webflow:     { label: "Webflow",       icon: Layout,       color: "#4353FF", desc: "Push posts into your Webflow CMS collection.",                  group: "cms",  live: true,  fields: ["api_token", "site_id", "collection_id"] },
  ghost:       { label: "Ghost",         icon: Ghost,        color: "#15171A", desc: "Auto-publish to your Ghost Admin API as drafts.",               group: "cms",  live: true,  fields: ["api_url", "admin_api_key"] },
  framer:      { label: "Framer",        icon: Frame,        color: "#0099FF", desc: "Sync blog posts to your Framer CMS collection.",                group: "cms",  live: true,  fields: ["site_url", "publish_webhook"] },
  wix:         { label: "Wix",           icon: Squirrel,     color: "#FAAD4D", desc: "Auto-publish to your Wix site blog.",                            group: "cms",  live: true,  fields: ["site_url", "publish_webhook"] },
  gohighlevel: { label: "GoHighLevel",   icon: ArrowUpCircle,color: "#28A745", desc: "Auto-publish to your GoHighLevel site.",                        group: "cms",  live: true,  fields: ["api_key", "location_id", "publish_webhook"] },
  duda:        { label: "Duda",          icon: Layers,       color: "#FF5A5F", desc: "Auto-publish to your Duda site.",                               group: "cms",  live: true,  fields: ["site_name", "api_token", "publish_webhook"] },
  bigcommerce: { label: "BigCommerce",   icon: Package,      color: "#34313F", desc: "Auto-publish to your BigCommerce store.",                       group: "cms",  live: true,  fields: ["store_hash", "access_token", "publish_webhook"] },
  api:         { label: "API",           icon: Code2,        color: "#6366F1", desc: "Send generated articles to your backend via a custom API.",     group: "dev",  live: true,  fields: ["url", "secret"] },
  webhook:     { label: "Custom Webhook",icon: Webhook,      color: "#10B981", desc: "POST blogs to any endpoint (Zapier, Make, n8n, custom CMS).",   group: "dev",  live: true,  fields: ["url", "secret"] },
  // Analytics — use connect_url to send user to Google's page first
  gsc:         { label: "Google Search Console", icon: BarChart3, color: "#4285F4", desc: "See real keyword rankings, clicks, impressions from GSC.", group: "analytics", live: true, fields: ["property_url", "service_account_json"], connect_url: "https://search.google.com/search-console", connect_label: "Open Google Search Console" },
  ga4:         { label: "Google Analytics 4",    icon: LineIcon,  color: "#FF6F00", desc: "See real traffic, sources, top pages from GA4.",            group: "analytics", live: true, fields: ["property_id", "service_account_json"], connect_url: "https://analytics.google.com/", connect_label: "Open Google Analytics" },
};

const GROUP_TITLES = {
  cms: "Publish to your site",
  dev: "Developer / Automation",
  analytics: "Analytics",
};

const FIELD_META = {
  site_url:             { label: "Site URL",            placeholder: "https://yoursite.com", required: true },
  username:             { label: "Username",            placeholder: "WordPress username",   required: true },
  app_password:         { label: "Application password",placeholder: "xxxx xxxx xxxx xxxx",  required: true, secret: true, help: <>Generate at <a className="text-[#3377FF] hover:underline" href="https://wordpress.org/documentation/article/application-passwords/" target="_blank" rel="noreferrer">Users → Profile → Application Passwords <ExternalLink className="w-3 h-3 inline-block" /></a></> },
  url:                  { label: "Endpoint URL",        placeholder: "https://hooks.zapier.com/...", required: true },
  secret:               { label: "Shared secret",       placeholder: "any random string",    secret: true, help: "Sent as the X-RankPilot-Secret header for verification on your side." },
  store_domain:         { label: "Shopify domain",      placeholder: "yourstore.myshopify.com", required: true },
  access_token:         { label: "Admin API access token", placeholder: "shpat_...",         required: true, secret: true, help: <>Create a Custom App in <a className="text-[#3377FF] hover:underline" href="https://help.shopify.com/en/manual/apps/app-types/custom-apps" target="_blank" rel="noreferrer">Shopify Admin <ExternalLink className="w-3 h-3 inline-block" /></a> with <code>write_content</code> scope.</> },
  blog_id:              { label: "Blog ID (optional)",  placeholder: "leave blank to use the default blog" },
  api_token:            { label: "API token",           placeholder: "Bearer token",         required: true, secret: true },
  site_id:              { label: "Site ID",             placeholder: "Webflow site ID",      required: true },
  collection_id:        { label: "Collection ID",       placeholder: "Webflow CMS collection ID", required: true, help: "Find in Webflow → CMS → Collection settings → API." },
  api_url:              { label: "API URL",             placeholder: "https://yourblog.ghost.io", required: true },
  admin_api_key:        { label: "Admin API key",       placeholder: "id:secret",            required: true, secret: true, help: "Format: id:secret. Get it from Ghost → Settings → Integrations → Add custom integration." },
  api_key:              { label: "API key",             placeholder: "API key",              required: true, secret: true },
  location_id:          { label: "Location ID",         placeholder: "GoHighLevel location ID", required: true },
  site_name:            { label: "Site name",           placeholder: "Duda site name",       required: true },
  store_hash:           { label: "Store hash",          placeholder: "BigCommerce store hash", required: true },
  property_url:         { label: "Property URL",        placeholder: "https://yoursite.com/", required: true, help: "The exact property URL registered in Google Search Console." },
  property_id:          { label: "GA4 Property ID",     placeholder: "451234567",            required: true, help: "Find it in GA4 → Admin → Property settings." },
  service_account_json: { label: "Service account JSON (optional)", placeholder: "paste JSON or leave blank", multiline: true, secret: true, help: "Optional — required for live data once Google API fetch is enabled." },
  publish_webhook:      { label: "Publish webhook URL (optional)", placeholder: "https://hooks.zapier.com/... or your endpoint", help: "Where to POST new blog payloads. Leave blank to save the connection without auto-publishing." },
};

export default function IntegrationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogKind, setDialogKind] = useState(null);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/integrations");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const connectedByKind = items.reduce((acc, it) => { (acc[it.kind] = acc[it.kind] || []).push(it); return acc; }, {});

  const remove = async (id) => {
    if (!window.confirm("Delete this integration?")) return;
    try { await api.delete(`/integrations/${id}`); toast.success("Removed"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const test = async (id) => {
    try {
      const { data } = await api.post(`/integrations/${id}/test`);
      if (data.ok) toast.success(data.note || "Connection successful");
      else toast.error(data.error || "Connection failed");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const patch = async (id, body) => {
    try { await api.patch(`/integrations/${id}`, body); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const groups = ["cms", "dev", "analytics"];

  return (
    <DashboardLayout
      eyebrow="Connect"
      title="Integrations"
      subtitle="Connect your website and publish blogs automatically. See real Google + LLM analytics."
    >
      <div className="space-y-10" data-testid="integrations-page">
        <GoogleConnectSection />
        <GoogleAdsLinkSection />

        {groups.map((g) => {
          const kinds = Object.entries(KIND_META).filter(([_, m]) => m.group === g);
          return (
            <section key={g} data-testid={`integrations-group-${g}`}>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="font-display text-lg font-semibold tracking-tight">{GROUP_TITLES[g]}</h2>
                <span className="text-xs text-zinc-600 tracking-widest uppercase">{kinds.length} options</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {kinds.map(([kind, meta]) => {
                  const connected = (connectedByKind[kind] || []).length;
                  return (
                    <IntegrationCard
                      key={kind}
                      kind={kind}
                      meta={meta}
                      connected={connected}
                      onConnect={() => { setEditing(null); setDialogKind(kind); }}
                    />
                  );
                })}
              </div>
            </section>
          );
        })}

        {/* Connected list */}
        <section>
          <h2 className="font-display text-lg font-semibold tracking-tight mb-4">Connected</h2>
          {loading ? (
            <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="border border-dashed border-zinc-800 rounded-sm p-12 text-center" data-testid="integrations-empty">
              <Plug className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400">No integrations yet. Connect one above to get started.</p>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="integrations-list">
              {items.map((item) => (
                <ConnectedRow
                  key={item.id}
                  item={item}
                  meta={KIND_META[item.kind]}
                  onTest={() => test(item.id)}
                  onEdit={() => { setEditing(item); setDialogKind(item.kind); }}
                  onDelete={() => remove(item.id)}
                  onToggleEnabled={() => patch(item.id, { enabled: !item.enabled })}
                  onToggleAuto={() => patch(item.id, { auto_publish: !item.auto_publish })}
                />
              ))}
            </ul>
          )}
        </section>

        <div className="border border-zinc-800 rounded-sm p-5 bg-zinc-950/40 flex items-start gap-3" data-testid="integrations-help">
          <Info className="w-4 h-4 text-[#3377FF] mt-0.5 flex-shrink-0" />
          <div className="text-sm text-zinc-400 leading-relaxed">
            <strong className="text-zinc-200">Auto-publish:</strong> when toggled on, every blog generated in <a href="/blog" className="text-[#3377FF] hover:underline">/blog</a> or by Autopilot is pushed automatically. CMS posts arrive as <em>drafts</em> — review and hit publish from your admin.
            <br />
            <strong className="text-zinc-200 mt-2 inline-block">Auto-apply Fix Kit:</strong> once a CMS is connected, head to <a href="/fix-kit" className="text-[#3377FF] hover:underline">Auto-Fix Kit</a> and hit "Apply to site" — patches are written straight into your CMS as a draft page/post.
          </div>
        </div>

        {/* Vercel / Static-host auto-publish helper (replaces legacy DNS host-with-us) */}
        <VercelPublishSection />
      </div>

      <IntegrationDialog
        open={!!dialogKind}
        onClose={() => { setDialogKind(null); setEditing(null); }}
        kind={dialogKind}
        editing={editing}
        onSaved={() => { setDialogKind(null); setEditing(null); refresh(); }}
      />
    </DashboardLayout>
  );
}

function IntegrationCard({ kind, meta, connected, onConnect }) {
  const Icon = meta.icon;
  const WEBHOOK_DISPATCHED = new Set(["framer", "wix", "gohighlevel", "duda", "bigcommerce"]);
  const isWebhookDispatched = WEBHOOK_DISPATCHED.has(kind);
  return (
    <button
      onClick={onConnect}
      className="group text-left bg-zinc-950 border border-zinc-800 hover:border-zinc-600 rounded-sm p-5 transition-all hover:-translate-y-0.5"
      data-testid={`integration-add-${kind}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-11 h-11 rounded-sm grid place-items-center flex-shrink-0" style={{ background: `${meta.color}15`, border: `1px solid ${meta.color}40` }}>
          <Icon className="w-5 h-5" style={{ color: meta.color }} />
        </div>
        <div className="flex items-center gap-1.5">
          {isWebhookDispatched && (
            <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-500 text-[10px] tracking-widest uppercase">Webhook-based</Badge>
          )}
          {connected > 0 && (
            <Badge className="rounded-sm bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 text-[10px] tracking-widest uppercase">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Connected{connected > 1 ? ` (${connected})` : ""}
            </Badge>
          )}
        </div>
      </div>
      <div className="font-display font-medium text-base">{meta.label}</div>
      <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed min-h-[2.5em]">{meta.desc}</p>
      <div className="mt-4 flex items-center gap-1.5 text-xs text-[#3377FF] group-hover:text-white transition-colors">
        Connect →
      </div>
    </button>
  );
}

function ConnectedRow({ item, meta, onTest, onEdit, onDelete, onToggleEnabled, onToggleAuto }) {
  if (!meta) return null;
  const Icon = meta.icon;
  const canAutoPublish = meta.live && meta.group !== "analytics";
  return (
    <li className="bg-zinc-950 border border-zinc-800 rounded-sm p-4 sm:p-5" data-testid={`integration-item-${item.id}`}>
      <div className="flex flex-wrap items-center gap-4">
        <div className="w-10 h-10 rounded-sm grid place-items-center flex-shrink-0" style={{ background: `${meta.color}15`, border: `1px solid ${meta.color}40` }}>
          <Icon className="w-5 h-5" style={{ color: meta.color }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium truncate">{item.name}</span>
            <Badge variant="outline" className="rounded-sm border-zinc-700 text-[10px] tracking-widest uppercase text-zinc-500">{meta.label}</Badge>
            {!item.enabled && <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-500 text-[10px]">DISABLED</Badge>}
            {canAutoPublish && item.auto_publish && item.enabled && (
              <Badge className="rounded-sm bg-[#0055FF]/15 text-[#3377FF] border border-[#0055FF]/30 text-[10px] tracking-widest uppercase">Auto-publish</Badge>
            )}
          </div>
          <div className="text-xs text-zinc-500 font-mono mt-1 truncate">
            {Object.entries(item.config_masked || {}).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join("  ·  ")}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {canAutoPublish && (
            <div className="flex items-center gap-2" title="Auto-publish new blog posts">
              <Switch checked={!!item.auto_publish} onCheckedChange={onToggleAuto} data-testid={`integration-auto-${item.id}`} />
              <span className="text-xs text-zinc-500 hidden sm:inline">Auto</span>
            </div>
          )}
          <Switch checked={!!item.enabled} onCheckedChange={onToggleEnabled} data-testid={`integration-enabled-${item.id}`} />
          <Button size="sm" variant="outline" onClick={onTest} className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm h-9" data-testid={`integration-test-${item.id}`}>
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Test
          </Button>
          <Button size="sm" variant="outline" onClick={onEdit} className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm h-9" data-testid={`integration-edit-${item.id}`}>
            <Settings className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="outline" onClick={onDelete} className="bg-zinc-950 hover:bg-red-950/40 border-zinc-800 hover:border-red-900/60 rounded-sm h-9 text-red-400" data-testid={`integration-delete-${item.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </li>
  );
}

function IntegrationDialog({ open, onClose, kind, editing, onSaved }) {
  const meta = kind ? KIND_META[kind] : null;
  const [name, setName] = useState("");
  const [config, setConfig] = useState({});
  const [autoPublish, setAutoPublish] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !meta) return;
    if (editing) {
      setName(editing.name || "");
      setConfig({});
      setAutoPublish(!!editing.auto_publish);
    } else {
      setName(meta.label);
      setConfig({});
      setAutoPublish(meta.live && meta.group !== "analytics");
    }
  }, [open, kind, editing, meta]);

  if (!kind || !meta) return null;
  const Icon = meta.icon;
  const canAutoPublish = meta.live && meta.group !== "analytics";

  const submit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const payload = { name };
        const cleanedCfg = Object.fromEntries(Object.entries(config).filter(([_, v]) => v != null && v !== ""));
        if (Object.keys(cleanedCfg).length) payload.config = { ...(editing.config_masked || {}), ...cleanedCfg };
        if (canAutoPublish) payload.auto_publish = autoPublish;
        await api.patch(`/integrations/${editing.id}`, payload);
        toast.success("Integration updated");
      } else {
        await api.post("/integrations", {
          kind, name, config,
          enabled: true,
          auto_publish: canAutoPublish ? autoPublish : false,
        });
        toast.success(`${meta.label} connected`);
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="integration-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm grid place-items-center" style={{ background: `${meta.color}15`, border: `1px solid ${meta.color}40` }}>
              <Icon className="w-4 h-4" style={{ color: meta.color }} />
            </div>
            {editing ? `Edit ${meta.label}` : `Connect ${meta.label}`}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">{meta.desc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="integration-form">
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Display name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required
                   placeholder="e.g. My main site"
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]"
                   data-testid="integration-name" />
          </div>

          {meta.connect_url && (
            <a
              href={meta.connect_url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-between gap-3 p-3 rounded-sm border border-[#0055FF]/40 bg-[#0055FF]/10 hover:bg-[#0055FF]/15 transition-colors group"
              data-testid={`integration-open-google-${kind}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-sm grid place-items-center" style={{ background: `${meta.color}25` }}>
                  <Icon className="w-4 h-4" style={{ color: meta.color }} />
                </div>
                <div>
                  <div className="text-sm font-medium text-zinc-100">{meta.connect_label}</div>
                  <div className="text-xs text-zinc-400 mt-0.5">Find your property and grant access, then paste the details below.</div>
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-[#3377FF] group-hover:text-white transition-colors flex-shrink-0" />
            </a>
          )}

          {meta.fields.map((f) => {
            const fm = FIELD_META[f] || { label: f };
            const val = config[f] || "";
            return (
              <div key={f}>
                <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">{fm.label}</Label>
                {fm.multiline ? (
                  <textarea
                    value={val}
                    onChange={(e) => setConfig({ ...config, [f]: e.target.value })}
                    placeholder={editing && fm.secret ? "•••••••• (unchanged)" : fm.placeholder}
                    rows={3}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#0055FF]"
                    data-testid={`integration-field-${f}`}
                  />
                ) : (
                  <Input
                    type={fm.secret ? "password" : "text"}
                    required={!!fm.required && !editing}
                    value={val}
                    onChange={(e) => setConfig({ ...config, [f]: e.target.value })}
                    placeholder={editing && fm.secret ? "•••••••• (unchanged)" : fm.placeholder}
                    className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]"
                    data-testid={`integration-field-${f}`}
                  />
                )}
                {fm.help && <p className="text-xs text-zinc-600 mt-1.5 leading-relaxed">{fm.help}</p>}
              </div>
            );
          })}

          {canAutoPublish && (
            <div className="flex items-start justify-between gap-4 border border-zinc-800 rounded-sm p-3 bg-zinc-950">
              <div>
                <div className="text-sm font-medium flex items-center gap-2"><Send className="w-3.5 h-3.5 text-[#3377FF]" /> Auto-publish new blogs</div>
                <div className="text-xs text-zinc-500 mt-1">Every generated blog will be sent here automatically.</div>
              </div>
              <Switch checked={autoPublish} onCheckedChange={setAutoPublish} data-testid="integration-auto-publish" />
            </div>
          )}

          {meta.group === "analytics" && (
            <div className="text-xs text-zinc-500 border border-zinc-800 rounded-sm p-3 bg-zinc-950">
              <strong className="text-zinc-300">Tip:</strong> after pasting your property, your Analytics dashboard will use this configuration. Real-time Google API fetch (replacing demo data) goes live next iteration — your config is preserved.
            </div>
          )}

          <DialogFooter className="pt-2 gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="integration-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Save changes" : "Connect")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function HostWithUsSection() {
  const { active } = useProjects();
  const domain = (() => {
    try {
      if (!active?.domain) return "yoursite.com";
      const u = new URL(active.domain.startsWith("http") ? active.domain : `https://${active.domain}`);
      return u.hostname;
    } catch { return active?.domain || "yoursite.com"; }
  })();
  const [subdomain, setSubdomain] = useState("blog");
  const [copied, setCopied] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const safeSub = subdomain || "blog";
  const txtLabel = safeSub === "@" ? "root" : safeSub;
  const expectedTxt = `rp-verify=${domain.split(".").join("-")}-${txtLabel}`;
  const fqdn = safeSub === "@" ? domain : `${safeSub}.${domain}`;
  const records = [
    { type: "CNAME", host: safeSub === "@" ? domain : safeSub, value: "cname.rankpilot.host", ttl: "3600" },
    { type: "TXT",   host: `_rankpilot-${txtLabel}`, value: expectedTxt, ttl: "3600" },
  ];

  const recordStatus = (type) => (verifyResult?.records || []).find(r => r.type === type);

  // Load cached verification status when domain/subdomain change
  useEffect(() => {
    if (!domain || domain === "yoursite.com") return;
    api.get(`/host/verify-dns/status?domain=${encodeURIComponent(domain)}&subdomain=${encodeURIComponent(safeSub)}`)
      .then(({ data }) => { if (data.checked) setVerifyResult(data); else setVerifyResult(null); })
      .catch(() => {});
  }, [domain, safeSub]);

  const copy = (val, key) => {
    navigator.clipboard.writeText(val);
    setCopied(key);
    setTimeout(() => setCopied(""), 1200);
  };

  const verify = async () => {
    if (!domain || domain === "yoursite.com") {
      toast.error("Set a project domain first.");
      return;
    }
    setVerifying(true);
    try {
      const { data } = await api.post("/host/verify-dns", { domain, subdomain: safeSub, project_id: active?.id });
      setVerifyResult(data);
      if (data.ok) toast.success(`✓ ${data.fqdn} is connected to RankPilot`);
      else if (data.partial) toast.warning(`${data.pass_count}/${data.total} DNS records resolved — try again in a few minutes.`);
      else toast.error("DNS not propagated yet. Wait a few minutes and try again.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setVerifying(false);
    }
  };

  const fullyConnected = verifyResult?.ok === true;

  return (
    <section className="border border-zinc-800 rounded-sm bg-zinc-950/40 overflow-hidden" data-testid="host-with-us">
      <div className="border-b border-zinc-800 px-6 py-5 flex items-start gap-4">
        <div className="w-11 h-11 rounded-sm bg-[#0055FF]/15 border border-[#0055FF]/40 grid place-items-center flex-shrink-0">
          <Server className="w-5 h-5 text-[#3377FF]" />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-semibold tracking-tight flex items-center gap-3">
            Don't see your CMS? Host with us.
            {fullyConnected && (
              <Badge className="rounded-sm bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 text-[10px] tracking-widest uppercase">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
              </Badge>
            )}
          </h2>
          <p className="text-sm text-zinc-400 leading-relaxed mt-1.5">
            Point a subdomain to RankPilot and we'll auto-publish every blog there. <strong className="text-zinc-300">Won't touch your existing site</strong> — only 2 DNS records needed.
          </p>
        </div>
      </div>

      <div className="px-6 py-5 space-y-5">
        {/* Subdomain picker */}
        <div className="flex items-end gap-3 flex-wrap" data-testid="host-subdomain-picker">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Subdomain (any name you like)</Label>
            <div className="flex items-center bg-zinc-950 border border-zinc-800 rounded-sm focus-within:border-[#0055FF] transition-colors">
              <Input
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value.replace(/[^a-z0-9-]/gi, "").toLowerCase())}
                placeholder="blog"
                className="h-11 bg-transparent border-0 rounded-sm focus-visible:ring-0 flex-1"
                data-testid="host-subdomain-input"
              />
              <span className="px-3 text-sm text-zinc-500 font-mono whitespace-nowrap">.{domain}</span>
            </div>
            <p className="text-xs text-zinc-600 mt-1.5">Examples: <code>blog</code>, <code>ai</code>, <code>content</code>. We'll host your AI blogs at <code className="text-[#3377FF]">{fqdn}</code>.</p>
          </div>
        </div>

        <ol className="space-y-3">
          <li className="flex gap-3" data-testid="host-step-1">
            <div className="w-6 h-6 rounded-sm bg-[#0055FF]/15 border border-[#0055FF]/40 grid place-items-center text-xs font-mono text-[#3377FF] flex-shrink-0">1</div>
            <div className="text-sm text-zinc-300 leading-relaxed pt-0.5">
              <strong className="text-zinc-100">Log in to your DNS provider</strong> — Cloudflare, GoDaddy, Namecheap, Route 53, or wherever you bought your domain.
            </div>
          </li>
          <li className="flex gap-3" data-testid="host-step-2">
            <div className="w-6 h-6 rounded-sm bg-[#0055FF]/15 border border-[#0055FF]/40 grid place-items-center text-xs font-mono text-[#3377FF] flex-shrink-0">2</div>
            <div className="text-sm text-zinc-300 leading-relaxed pt-0.5">
              <strong className="text-zinc-100">Add these 2 DNS records</strong> on <span className="font-mono text-[#3377FF]">{domain}</span>:
            </div>
          </li>
        </ol>

        <div className="border border-zinc-800 rounded-sm overflow-hidden" data-testid="dns-records">
          <div className="grid grid-cols-12 bg-zinc-950 border-b border-zinc-800 px-4 py-2.5 text-[10px] tracking-widest uppercase text-zinc-500 font-medium">
            <div className="col-span-1">Type</div>
            <div className="col-span-2">Host</div>
            <div className="col-span-6">Value</div>
            <div className="col-span-1">TTL</div>
            <div className="col-span-2 text-right pr-1">Status</div>
          </div>
          {records.map((r, i) => {
            const st = recordStatus(r.type);
            return (
              <div key={i} className="grid grid-cols-12 items-center px-4 py-3 text-sm border-b border-zinc-900 last:border-0 font-mono" data-testid={`dns-record-${r.type.toLowerCase()}`}>
                <div className="col-span-1"><Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-300 text-[10px]">{r.type}</Badge></div>
                <div className="col-span-2 text-zinc-300 truncate" title={r.host}>{r.host}</div>
                <div className="col-span-6 text-zinc-400 truncate" title={r.value}>{r.value}</div>
                <div className="col-span-1 text-zinc-500 text-xs">{r.ttl}</div>
                <div className="col-span-2 flex items-center justify-end gap-1.5">
                  {st ? (
                    st.ok ? (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-[#10B981]" data-testid={`dns-status-${r.type.toLowerCase()}-ok`}>
                        <CheckCircle2 className="w-3 h-3" /> OK
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-amber-400" title={st.error || "Mismatch"} data-testid={`dns-status-${r.type.toLowerCase()}-fail`}>
                        <X className="w-3 h-3" /> Pending
                      </span>
                    )
                  ) : null}
                  <button
                    onClick={() => copy(r.value, `${r.type}-${i}`)}
                    className="text-zinc-500 hover:text-zinc-200 transition-colors p-1.5 rounded-sm hover:bg-zinc-900"
                    data-testid={`dns-copy-${r.type.toLowerCase()}`}
                    aria-label={`Copy ${r.type} value`}
                  >
                    {copied === `${r.type}-${i}` ? <Check className="w-3.5 h-3.5 text-[#10B981]" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <ol start={3} className="space-y-3">
          <li className="flex gap-3" data-testid="host-step-3">
            <div className="w-6 h-6 rounded-sm bg-[#0055FF]/15 border border-[#0055FF]/40 grid place-items-center text-xs font-mono text-[#3377FF] flex-shrink-0">3</div>
            <div className="text-sm text-zinc-300 leading-relaxed pt-0.5">
              <strong className="text-zinc-100">Wait for propagation</strong> — usually 5–15 minutes, sometimes up to 24h depending on your TTL.
            </div>
          </li>
          <li className="flex gap-3" data-testid="host-step-4">
            <div className="w-6 h-6 rounded-sm bg-[#0055FF]/15 border border-[#0055FF]/40 grid place-items-center text-xs font-mono text-[#3377FF] flex-shrink-0">4</div>
            <div className="text-sm text-zinc-300 leading-relaxed pt-0.5">
              <strong className="text-zinc-100">Click Confirm</strong> — we'll live-check <span className="font-mono text-[#3377FF]">{fqdn}</span>. When both records resolve, auto-publishing turns on instantly.
            </div>
          </li>
        </ol>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
          <Button
            onClick={verify}
            disabled={verifying || !domain || domain === "yoursite.com"}
            className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11 px-5"
            data-testid="host-confirm-dns"
          >
            {verifying ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking DNS…</>
            ) : fullyConnected ? (
              <><CheckCircle2 className="w-4 h-4 mr-2" /> Verified — re-check</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-2" /> Confirm DNS</>
            )}
          </Button>
          {verifyResult && (
            <div
              className={`text-sm px-3.5 py-2.5 rounded-sm border flex-1 ${
                verifyResult.ok
                  ? "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]"
                  : verifyResult.partial
                    ? "border-amber-700/40 bg-amber-950/30 text-amber-300"
                    : "border-red-900/40 bg-red-950/30 text-red-400"
              }`}
              data-testid="host-confirm-result"
            >
              {verifyResult.ok ? (
                <strong>✓ {fqdn} is fully connected to RankPilot. Auto-publishing is live.</strong>
              ) : verifyResult.partial ? (
                <span><strong>{verifyResult.pass_count}/{verifyResult.total} records resolved.</strong> Wait a few more minutes and re-check — DNS is still propagating.</span>
              ) : (
                <span><strong>DNS not propagated yet.</strong> Add both records on <span className="font-mono">{domain}</span> and re-check in a few minutes.</span>
              )}
              {verifyResult.checked_at && (
                <div className="text-[10px] tracking-widest uppercase opacity-70 mt-1">Last checked: {new Date(verifyResult.checked_at).toLocaleString()}</div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-zinc-800">
          <a href={`https://www.whatsmydns.net/#CNAME/${fqdn}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#3377FF] hover:text-white transition-colors" data-testid="host-check-dns">
            <ExternalLink className="w-3 h-3" /> Check DNS propagation
          </a>
          <span className="text-zinc-700">·</span>
          <a href="mailto:support@myrankpilotai.com" className="inline-flex items-center gap-1.5 text-xs text-[#3377FF] hover:text-white transition-colors" data-testid="host-contact">
            <ExternalLink className="w-3 h-3" /> Need help? Talk to us
          </a>
        </div>
      </div>
    </section>
  );
}


// ============== Google OAuth Connect Section ==============
function GoogleConnectSection() {
  const [status, setStatus] = useState(null); // {connected, google_email}
  const [props, setProps] = useState(null);    // {gsc_sites, ga4_properties}
  const [selection, setSelection] = useState({ gsc_site_url: "", ga4_property_id: "" });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: st } = await api.get("/google/oauth/status");
      setStatus(st);
      if (st.connected) {
        const [{ data: p }, { data: sel }] = await Promise.all([
          api.get("/google/properties"),
          api.get("/google/selection"),
        ]);
        setProps(p);
        setSelection({
          gsc_site_url: sel.gsc_site_url || "",
          ga4_property_id: sel.ga4_property_id || "",
        });
      } else {
        setProps(null);
      }
    } catch (e) {
      // ignore — not critical
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Handle redirect-back from Google
    const params = new URLSearchParams(window.location.search);
    if (params.get("google_connected") === "1") {
      toast.success("Google connected. Pick your Search Console site + GA4 property below.");
      window.history.replaceState({}, "", window.location.pathname);
    }
    const err = params.get("google_error");
    if (err) {
      toast.error(`Google connect failed: ${err.replace(/_/g, " ")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/google/oauth/start?return_to=/integrations");
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google? Your selected site/property will be cleared.")) return;
    try {
      await api.post("/google/oauth/disconnect");
      toast.success("Google disconnected");
      setStatus({ connected: false });
      setProps(null);
      setSelection({ gsc_site_url: "", ga4_property_id: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/google/selection", selection);
      toast.success("Saved. Your Analytics page now shows real data.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="google-connect-section" className="border border-zinc-800 rounded-sm p-5 bg-gradient-to-br from-zinc-950 to-zinc-950/40">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-sm grid place-items-center flex-shrink-0" style={{ background: "#4285F415", border: "1px solid #4285F440" }}>
          <BarChart3 className="w-6 h-6" style={{ color: "#4285F4" }} />
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-lg font-semibold tracking-tight">Connect Google (Search Console + GA4)</h2>
            {status?.connected && (
              <Badge className="rounded-sm bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 text-[10px] tracking-widest uppercase">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
              </Badge>
            )}
          </div>
          <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
            One-click OAuth — we read your real keyword rankings, clicks, impressions, sessions, top pages and traffic sources. Live data appears on the <a href="/analytics" className="text-[#3377FF] hover:underline">Analytics</a> page.
          </p>
          {status?.connected && status?.google_email && (
            <p className="text-xs text-zinc-500 mt-2 font-mono">Signed in as {status.google_email}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!status?.connected ? (
            <Button onClick={connect} disabled={connecting || loading} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="google-connect-btn">
              {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />} Connect Google
            </Button>
          ) : (
            <>
              <Button onClick={load} variant="outline" className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm" data-testid="google-refresh-btn">
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button onClick={disconnect} variant="outline" className="bg-zinc-950 hover:bg-red-950/40 border-zinc-800 hover:border-red-900/60 rounded-sm text-red-400" data-testid="google-disconnect-btn">
                <X className="w-4 h-4 mr-1.5" /> Disconnect
              </Button>
            </>
          )}
        </div>
      </div>

      {status?.connected && props && (
        <div className="grid sm:grid-cols-2 gap-4 mt-5 pt-5 border-t border-zinc-800" data-testid="google-property-pickers">
          <div>
            <Label className="text-[11px] tracking-widest uppercase text-zinc-500">Search Console site</Label>
            <select
              value={selection.gsc_site_url}
              onChange={(e) => setSelection((s) => ({ ...s, gsc_site_url: e.target.value }))}
              className="mt-2 w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm focus:border-[#0055FF] focus:outline-none"
              data-testid="google-gsc-select"
            >
              <option value="">— select a site —</option>
              {(props.gsc_sites || []).map((s) => (
                <option key={s.site_url} value={s.site_url}>{s.site_url} {s.permission_level ? `(${s.permission_level})` : ""}</option>
              ))}
            </select>
            {!(props.gsc_sites || []).length && (
              <p className="text-[11px] text-zinc-500 mt-2">No sites found. Verify a property in <a href="https://search.google.com/search-console" target="_blank" rel="noreferrer" className="text-[#3377FF] hover:underline">Search Console</a> first.</p>
            )}
          </div>
          <div>
            <Label className="text-[11px] tracking-widest uppercase text-zinc-500">GA4 property</Label>
            <select
              value={selection.ga4_property_id}
              onChange={(e) => setSelection((s) => ({ ...s, ga4_property_id: e.target.value }))}
              className="mt-2 w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm focus:border-[#0055FF] focus:outline-none"
              data-testid="google-ga4-select"
            >
              <option value="">— select a property —</option>
              {(props.ga4_properties || []).map((p) => (
                <option key={p.property_id} value={p.property_id}>{p.display_name || p.property_id} (ID: {p.property_id})</option>
              ))}
            </select>
            {!(props.ga4_properties || []).length && (
              <p className="text-[11px] text-zinc-500 mt-2">No GA4 properties found. Create one in <a href="https://analytics.google.com" target="_blank" rel="noreferrer" className="text-[#3377FF] hover:underline">Google Analytics</a> first.</p>
            )}
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="google-save-selection-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Save selection
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}


// ============== Google Ads Customer ID linker ==============
function GoogleAdsLinkSection() {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [linked, setLinked] = useState(null);
  const [error, setError] = useState(null);
  const [customerId, setCustomerId] = useState("");
  const [loginCustomerId, setLoginCustomerId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/google-ads/accounts");
      setAccounts(data.accounts || []);
      setLinked(data.linked || null);
      if (data.linked?.customer_id) setCustomerId(data.linked.customer_id);
      if (data.linked?.login_customer_id) setLoginCustomerId(data.linked.login_customer_id);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      const msg = formatApiError(e);
      setError({ code, msg });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const link = async () => {
    setBusy(true);
    try {
      await api.post("/google-ads/link-customer", {
        customer_id: customerId,
        login_customer_id: loginCustomerId || null,
      });
      toast.success("Google Ads account linked. You can now push campaigns live.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const formatId = (id) => id.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");

  return (
    <section data-testid="google-ads-link-section" className="border border-zinc-800 rounded-sm p-5 bg-gradient-to-br from-zinc-950 to-zinc-950/40">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-sm grid place-items-center flex-shrink-0" style={{ background: "#FBBC0415", border: "1px solid #FBBC0440" }}>
          <Megaphone className="w-6 h-6" style={{ color: "#FBBC04" }} />
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-lg font-semibold tracking-tight">Link Google Ads account</h2>
            {linked?.customer_id && (
              <Badge className="rounded-sm bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 text-[10px] tracking-widest uppercase">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Linked
              </Badge>
            )}
          </div>
          <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
            Push generated campaigns directly into your real Google Ads account (created in PAUSED state — you review and resume from Google's UI).
          </p>
          {linked?.customer_id && (
            <p className="text-xs text-zinc-500 mt-2 font-mono">Linked: {formatId(linked.customer_id)}{linked.login_customer_id ? ` · via MCC ${formatId(linked.login_customer_id)}` : ""}</p>
          )}
        </div>
      </div>

      {loading && <div className="text-sm text-zinc-500 mt-4 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading your Google Ads accounts…</div>}

      {error && (
        <div className="mt-4 text-sm border border-amber-700/40 bg-amber-950/20 rounded-sm p-3 text-amber-200" data-testid="google-ads-error">
          <div className="font-medium mb-1">Couldn't load Google Ads accounts</div>
          <p className="text-amber-200/80 text-xs">{error.msg}</p>
          {error.code === "google_not_linked" && (
            <p className="text-xs mt-2">→ Connect your Google account in the section above first, or paste a Customer ID manually below.</p>
          )}
          {error.code === "ads_scope_missing" && (
            <p className="text-xs mt-2">→ Disconnect Google above, then reconnect — we'll request the Ads permission this time.</p>
          )}
          {error.code === "dev_token_not_approved" && (
            <p className="text-xs mt-2">→ Your developer token is still on Explorer Access. Apply for Basic Access at <a className="text-[#3377FF] hover:underline" target="_blank" rel="noreferrer" href="https://ads.google.com/aw/apicenter">Google Ads API Center</a>.</p>
          )}
        </div>
      )}

      {!loading && (
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          {!error && accounts.length > 0 && (
            <div className="sm:col-span-2">
              <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Pick from your accounts</Label>
              <div className="flex flex-wrap gap-2">
                {accounts.map((a) => (
                  <button key={a.customer_id} type="button"
                          onClick={() => setCustomerId(a.customer_id)}
                          className={`text-xs px-3 py-2 rounded-sm border transition-colors ${customerId === a.customer_id ? "bg-[#0055FF]/20 border-[#0055FF] text-white" : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-zinc-700"}`}
                          data-testid={`gads-account-${a.customer_id}`}>
                    <span className="font-medium">{a.name}</span>
                    <span className="text-zinc-500 ml-2 font-mono">{formatId(a.customer_id)}</span>
                    {a.is_manager && <span className="ml-2 text-[10px] bg-amber-500/20 text-amber-400 px-1 rounded">MCC</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Customer ID (10 digits)</Label>
            <Input value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="1234567890"
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm font-mono" data-testid="gads-customer-id" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Manager (MCC) ID — optional</Label>
            <Input value={loginCustomerId} onChange={(e) => setLoginCustomerId(e.target.value)} placeholder="(only if access is via MCC)"
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm font-mono" data-testid="gads-login-customer-id" />
          </div>
          <div className="sm:col-span-2">
            <Button onClick={link} disabled={busy || !customerId} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11" data-testid="gads-link-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />}
              {linked?.customer_id ? "Update link" : "Link account"}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}


// ============== Vercel / Static-host auto-publish helper ==============
function VercelPublishSection() {
  const { active } = useProjects();
  const domain = (() => {
    try {
      if (!active?.domain) return "yoursite.com";
      const u = new URL(active.domain.startsWith("http") ? active.domain : `https://${active.domain}`);
      return u.hostname;
    } catch { return active?.domain || "yoursite.com"; }
  })();
  const [copied, setCopied] = useState("");
  const copy = (val, key) => {
    navigator.clipboard.writeText(val);
    setCopied(key);
    setTimeout(() => setCopied(""), 1200);
  };

  const sampleEndpoint = `https://${domain}/api/rankpilot-webhook`;
  const sampleSecret = "rp_" + Math.random().toString(36).slice(2, 10);

  const nextJsRoute = `// /app/api/rankpilot-webhook/route.js  (Next.js App Router on Vercel)
// REMINDER: store SECRET in Vercel env vars, never hardcode.
import { NextResponse } from "next/server";
import { writeFile } from "fs/promises";
import path from "path";

export async function POST(req) {
  const secret = req.headers.get("x-rankpilot-secret");
  if (secret !== process.env.RANKPILOT_SECRET) {
    return NextResponse.json({ error: "bad secret" }, { status: 401 });
  }
  const post = await req.json();
  // Option 1: commit MD to your git repo (triggers Vercel rebuild)
  // Option 2: store in DB and render with revalidateTag()
  // Quick start — write to /content/blog/{slug}.md if writable (local dev only):
  // await writeFile(path.join(process.cwd(), "content/blog", post.slug + ".md"), post.markdown);
  return NextResponse.json({ ok: true, slug: post.slug });
}`;

  const payloadShape = `{
  "slug": "ai-seo-guide-2026",
  "title": "AI SEO Guide 2026",
  "meta_description": "...",
  "markdown": "# AI SEO Guide...",
  "html": "<h1>...</h1>",
  "tags": ["seo", "ai"],
  "featured_image_url": "https://...",
  "faq": [{ "q": "...", "a": "..." }],
  "published_at": "2026-02-09T12:00:00Z"
}`;

  return (
    <section className="border border-zinc-800 rounded-sm bg-zinc-950/40 overflow-hidden" data-testid="vercel-publish-section">
      <div className="border-b border-zinc-800 px-6 py-5 flex items-start gap-4">
        <div className="w-11 h-11 rounded-sm bg-white/5 border border-white/10 grid place-items-center flex-shrink-0">
          <Server className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-semibold tracking-tight flex items-center gap-3">
            Already on Vercel / Netlify / your own site?
            <Badge variant="outline" className="rounded-sm border-[#10B981]/40 text-[#10B981] text-[10px] tracking-widest uppercase">Recommended</Badge>
          </h2>
          <p className="text-sm text-zinc-400 leading-relaxed mt-1.5">
            Skip DNS — auto-publish to your existing site via a webhook. Works with Next.js, Astro, SvelteKit, Eleventy, anything serverless. No new hosting needed.
          </p>
        </div>
      </div>

      <div className="px-6 py-5 space-y-5">
        <ol className="space-y-4">
          <li className="flex gap-3" data-testid="vercel-step-1">
            <div className="w-6 h-6 rounded-sm bg-[#0055FF]/15 border border-[#0055FF]/40 grid place-items-center text-xs font-mono text-[#3377FF] flex-shrink-0">1</div>
            <div className="text-sm text-zinc-300 leading-relaxed pt-0.5">
              <strong className="text-zinc-100">Add an API route to your site</strong> at e.g. <code className="text-[#3377FF]">/api/rankpilot-webhook</code> that receives blog payloads.
            </div>
          </li>
        </ol>

        <div className="bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800 text-[10px] tracking-widest uppercase text-zinc-500">
            <span>Next.js App Router route example</span>
            <button onClick={() => copy(nextJsRoute, "code")} className="text-zinc-500 hover:text-zinc-200 transition-colors" data-testid="vercel-copy-code">
              {copied === "code" ? <Check className="w-3.5 h-3.5 text-[#10B981]" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
          <pre className="text-xs text-zinc-300 font-mono p-4 overflow-x-auto leading-relaxed">{nextJsRoute}</pre>
        </div>

        <ol start={2} className="space-y-4">
          <li className="flex gap-3" data-testid="vercel-step-2">
            <div className="w-6 h-6 rounded-sm bg-[#0055FF]/15 border border-[#0055FF]/40 grid place-items-center text-xs font-mono text-[#3377FF] flex-shrink-0">2</div>
            <div className="text-sm text-zinc-300 leading-relaxed pt-0.5">
              <strong className="text-zinc-100">Set a secret env var on Vercel</strong> — name it <code className="text-[#3377FF]">RANKPILOT_SECRET</code>. We'll send it as the <code>x-rankpilot-secret</code> header on every request.
            </div>
          </li>
          <li className="flex gap-3" data-testid="vercel-step-3">
            <div className="w-6 h-6 rounded-sm bg-[#0055FF]/15 border border-[#0055FF]/40 grid place-items-center text-xs font-mono text-[#3377FF] flex-shrink-0">3</div>
            <div className="text-sm text-zinc-300 leading-relaxed pt-0.5">
              <strong className="text-zinc-100">Connect the Custom Webhook above</strong> with these values:
            </div>
          </li>
        </ol>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
            <Label className="text-[10px] tracking-widest uppercase text-zinc-500">Endpoint URL</Label>
            <div className="flex items-center gap-2 mt-2">
              <code className="text-sm text-zinc-200 font-mono truncate flex-1">{sampleEndpoint}</code>
              <button onClick={() => copy(sampleEndpoint, "url")} className="text-zinc-500 hover:text-zinc-200 transition-colors p-1" data-testid="vercel-copy-url">
                {copied === "url" ? <Check className="w-3.5 h-3.5 text-[#10B981]" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
            <Label className="text-[10px] tracking-widest uppercase text-zinc-500">Shared secret (example)</Label>
            <div className="flex items-center gap-2 mt-2">
              <code className="text-sm text-zinc-200 font-mono truncate flex-1">{sampleSecret}</code>
              <button onClick={() => copy(sampleSecret, "secret")} className="text-zinc-500 hover:text-zinc-200 transition-colors p-1" data-testid="vercel-copy-secret">
                {copied === "secret" ? <Check className="w-3.5 h-3.5 text-[#10B981]" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </div>

        <details className="border border-zinc-800 rounded-sm bg-zinc-950" data-testid="vercel-payload-shape">
          <summary className="px-4 py-2.5 text-xs tracking-widest uppercase text-zinc-400 cursor-pointer hover:text-zinc-200">View payload shape</summary>
          <pre className="text-xs text-zinc-300 font-mono p-4 border-t border-zinc-800 overflow-x-auto">{payloadShape}</pre>
        </details>

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-zinc-800">
          <a href="https://vercel.com/docs/functions/api-routes" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#3377FF] hover:text-white transition-colors" data-testid="vercel-docs-fn">
            <ExternalLink className="w-3 h-3" /> Vercel API Routes docs
          </a>
          <span className="text-zinc-700">·</span>
          <a href="https://vercel.com/docs/projects/environment-variables" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#3377FF] hover:text-white transition-colors" data-testid="vercel-docs-env">
            <ExternalLink className="w-3 h-3" /> Set environment variables
          </a>
          <span className="text-zinc-700">·</span>
          <a href="mailto:support@myrankpilotai.com" className="inline-flex items-center gap-1.5 text-xs text-[#3377FF] hover:text-white transition-colors" data-testid="vercel-contact">
            <ExternalLink className="w-3 h-3" /> Need help? Talk to us
          </a>
        </div>
      </div>
    </section>
  );
}
